import { isBlankConditionValue } from 'containers/OfferLetterTemplates/constants';

import { ConditionField, ConditionalBlock, FormField, OfferLetterFormData } from './types';

/**
 * AXHE-4407 — WHERE the fields an answer reveals are rendered.
 *
 * `visibility.ts` decides which fields the form asks for; this decides where
 * each one goes. The two are deliberately separate: nothing here can add or
 * remove a field, so a bug in the ordering can misplace an input but can never
 * drop one from a legally binding document.
 *
 * ## The defect
 *
 * The form rendered two flat groups in fixed order: every variable at the top,
 * then the condition inputs under a "Section details" divider at the bottom.
 * Answering a condition question therefore revealed a field at the TOP of the
 * panel while the control that revealed it sat at the BOTTOM — the recruiter
 * answered a question, saw nothing change where they were looking, and then
 * failed the readiness gate on a field they never saw. Design's words: "very
 * confusing for the user to get an error on fields that appear above when they
 * thought they'd finished filling all."
 *
 * The prototype's shape is the fix: "Sign-on bonus → Included" reveals "Enter
 * sign-on bonus amount" in the very next row, so cause and effect are adjacent
 * and the reveal is self-evident.
 *
 * ## Which control "revealed" a field
 *
 * Two things can withdraw a field (see `isFieldVisible`), and both count as a
 * reveal when they let go:
 *
 *   1. its own `visible_when` — "ask for Relocation Package only when Work
 *      Location says relocation";
 *   2. its `block_occurrences` — it lives ONLY inside document sections these
 *      answers exclude, so answering the section's own question is what puts it
 *      on screen. Rule 1 is checked first: a field-specific rule is a more
 *      direct cause than a section-wide one.
 *
 * The chosen anchor is the FIRST data key, in payload order, that names a
 * control actually on screen — first `visible_when` entry, else the first block
 * enclosing an occurrence. First, not all, and not duplicated:
 *
 *   * duplicating the input would put two controls on screen writing the same
 *     answer, and a recruiter who edits the second one would watch the first
 *     change under them;
 *   * leaving a multi-key field in the main list is the defect this exists to
 *     remove, just narrowed to the fields with more than one rule.
 *
 * ## What never moves
 *
 * A field that is always asked for stays exactly where the payload put it. The
 * set that moves is precisely the set that can be withdrawn — so a field the
 * recruiter can already see cannot be reshuffled by an answer given later, and
 * a reveal only ever INSERTS. That is why the section rule requires EVERY
 * occurrence to sit inside a block: one top-level occurrence and the field is
 * asked for unconditionally, so it has no reveal to be adjacent to.
 *
 * Both anchor rules read the payload's rules only, never the current answers,
 * for the same reason.
 *
 * ## Residual, stated rather than hidden
 *
 * A control has to be on screen to be rendered under, so the anchors are
 * resolved against the fields the form is currently asking for. A mapped field
 * that is ITSELF contingent and ALSO gates another field can therefore appear
 * and collect its dependants as it does. That shape is rare, and the
 * alternative is worse: anchoring to an absent control would strand the
 * dependant at the bottom of the list until the control showed up, which is a
 * move either way and a worse position in the meantime.
 *
 * A key two fields carry is decided by the last one holding a value, so the
 * dependants follow that field. Answering a later twin while an earlier one
 * decides moves them from under the earlier twin to under the later one, and
 * they stay on screen.
 */

/**
 * One condition input and the fields its answer reveals, in render order.
 *
 * The input is a `ConditionField`, or a variable whose data key also triggers
 * a block (HIR-375 change 4): that variable is the key's one control, so it
 * renders here with its typed input while its answer still travels as the
 * field's own value.
 */
export interface ConditionGroup {
  field: ConditionField | FormField;
  revealed: FormField[];
}

/** The key a condition input answers: its own, or a variable's data key. */
export const conditionInputKey = (field: ConditionField | FormField): string =>
  field.kind === 'condition' ? field.key : field.data_key ?? field.key;

export interface FormLayout {
  /**
   * The main list: every field no on-screen control reveals, each followed
   * immediately by the fields its own answer reveals.
   */
  fields: FormField[];
  /** The "Section details" group: each condition input with what it reveals. */
  conditionGroups: ConditionGroup[];
  /**
   * The key of each mapped field whose value decides its data key: the sole
   * carrier, or of two carriers the last one holding a value.
   */
  deciders: Set<string>;
}

/**
 * The data key whose control gates this field via the field's own rules, or
 * null. `visible_when` is OR-combined, so any entry is a legitimate cause and
 * the first one on screen is chosen.
 *
 * A field is never anchored to its own data key: a mapped field whose condition
 * tests the key it writes to would otherwise be nested under itself.
 */
const ownAnchor = (field: FormField, controlKeys: Set<string>): string | null =>
  field.visible_when
    .map(condition => condition.data_key)
    .find(dataKey => dataKey !== field.data_key && controlKeys.has(dataKey)) ?? null;

/**
 * The data key whose control gates this field through the sections it lives in,
 * or null.
 *
 * Only for a field EVERY occurrence of which sits inside a section — the exact
 * condition under which `confinedToExcludedSections` can withdraw it. A field
 * with an occurrence at top level (an empty chain), or with no recorded
 * occurrences at all, is asked for whatever the answers say, so it is not
 * revealed by anything and does not move.
 */
const sectionAnchor = (
  field: FormField,
  blocks: ConditionalBlock[],
  controlKeys: Set<string>,
): string | null => {
  if (field.block_occurrences.length === 0) return null;
  if (field.block_occurrences.some(chain => chain.length === 0)) return null;

  const conditionsByBlock = new Map(blocks.map(block => [block.key, block.included_when]));

  // Chains run outermost-first and occurrences are in document order, so this
  // walks the enclosing blocks in the order the template declares them.
  return (
    field.block_occurrences
      .flat()
      .flatMap(blockKey => conditionsByBlock.get(blockKey) ?? [])
      .map(condition => condition.data_key)
      .find(dataKey => dataKey !== field.data_key && controlKeys.has(dataKey)) ?? null
  );
};

const anchorFor = (
  field: FormField,
  blocks: ConditionalBlock[],
  controlKeys: Set<string>,
): string | null =>
  ownAnchor(field, controlKeys) ?? sectionAnchor(field, blocks, controlKeys);

/**
 * Where each asked-for field renders.
 *
 * @param form the payload — read for its rules (`conditional_blocks`,
 *   `condition_fields`) only; which fields are asked for is `askedFields`.
 * @param askedFields `visibleFields(...)`, already filtered. Passed in rather
 *   than re-resolved so this cannot reach a different answer than the caller
 *   about what is on screen.
 * @param values the recruiter's field values, the same ones `visibleFields`
 *   read. They name the field whose value decides a key two fields carry.
 */
export const formLayout = (
  form: OfferLetterFormData,
  askedFields: FormField[],
  values: Record<string, string> = {},
): FormLayout => {
  // The controls a field can be rendered under: every condition input (all of
  // which render) and the data key of every mapped field that is on screen.
  const controlKeys = new Set<string>(form.condition_fields.map(field => field.key));
  askedFields.forEach(field => {
    if (field.data_key) controlKeys.add(field.data_key);
  });

  const revealedBy = new Map<string, FormField[]>();
  const anchored = new Set<string>();

  askedFields.forEach(field => {
    const anchor = anchorFor(field, form.conditional_blocks, controlKeys);
    if (anchor == null) return;

    revealedBy.set(anchor, [...(revealedBy.get(anchor) ?? []), field]);
    anchored.add(field.key);
  });

  // A revealed field can reveal further fields, so this is a walk rather than
  // one lookup. `emitted` is what makes it total: it stops a pair of fields
  // that gate each other from recursing, and lets the sweep at the bottom find
  // anything such a cycle left unplaced.
  //
  // A key two fields carry is decided by the last one holding a value: the
  // client overlay (`resolveDataKeyValues`) and the server's
  // (FieldConditionEvaluator#mapped_field_values) both let the later answered
  // field win, and both read every field, withdrawn ones included. So the
  // dependants render under that field. When it is withdrawn they render under
  // no carrier, and the sweep at the bottom places them; a visible carrier
  // showing another value would contradict why they appeared.
  const decider = new Map<string, string>();
  askedFields.forEach(field => {
    if (field.data_key) decider.set(field.data_key, field.key);
  });
  form.fields.forEach(field => {
    if (!field.data_key) return;
    const value = isBlankConditionValue(values[field.key]) ? field.value : values[field.key];
    if (!isBlankConditionValue(value)) decider.set(field.data_key, field.key);
  });
  const decides = (field: FormField) =>
    !!field.data_key && decider.get(field.data_key) === field.key;

  const emitted = new Set<string>();
  const emit = (field: FormField, into: FormField[]) => {
    if (emitted.has(field.key)) return;
    emitted.add(field.key);
    into.push(field);
    if (!field.data_key || !decides(field)) return;
    (revealedBy.get(field.data_key) ?? []).forEach(dependant => emit(dependant, into));
  };

  // HIR-375 change 4. A variable whose data key a block tests moves into the
  // conditional section with its dependants — unless an answer reveals it,
  // in which case it stays under that answer.
  //
  // Only the key's sole variable moves. Two template fields can carry one data
  // key, each with its own input and its own printed value; promoting one would
  // leave the other in the main list holding the key's dependants, and the
  // promoted group would render empty. So a shared key keeps both in the main
  // list, dependants under the field whose value decides the key.
  const triggerKeys = form.conditional_blocks.flatMap(block =>
    block.included_when.map(condition => condition.data_key),
  );
  const carriers = new Map<string, number>();
  askedFields.forEach(field => {
    if (field.data_key) carriers.set(field.data_key, (carriers.get(field.data_key) ?? 0) + 1);
  });
  const triggerVariables = new Map<string, FormField>();
  askedFields.forEach(field => {
    if (!field.data_key || anchored.has(field.key) || carriers.get(field.data_key) !== 1) return;
    if (triggerKeys.includes(field.data_key)) triggerVariables.set(field.data_key, field);
  });
  const promoted = new Set([...triggerVariables.values()].map(field => field.key));

  const fields: FormField[] = [];
  askedFields.forEach(field => {
    // Anchored fields are emitted by their anchor, wherever that renders.
    if (!anchored.has(field.key) && !promoted.has(field.key)) emit(field, fields);
  });

  // Every condition input in the order the blocks first test its key, then any
  // the blocks do not name (a key only a stray field condition tests).
  const conditionsByKey = new Map(form.condition_fields.map(field => [field.key, field]));
  const inputs: (ConditionField | FormField)[] = [];
  const placed = new Set<string>();
  const place = (key: string) => {
    const input = conditionsByKey.get(key) ?? triggerVariables.get(key);
    if (input == null || placed.has(key)) return;
    placed.add(key);
    inputs.push(input);
  };
  triggerKeys.forEach(place);
  form.condition_fields.forEach(field => place(field.key));

  const conditionGroups = inputs.map(field => {
    const revealed: FormField[] = [];
    if (field.kind !== 'condition') emitted.add(field.key);
    if (field.kind !== 'condition' && !decides(field)) return { field, revealed };
    (revealedBy.get(conditionInputKey(field)) ?? []).forEach(dependant => emit(dependant, revealed));
    return { field, revealed };
  });

  // Nothing may fall out of the form because of where it was going to be put.
  // Two fields each gated by the other's data key are both anchored and neither
  // is a root, so neither walk above reaches them; they render in payload order
  // at the foot of the main list, which is where they render today.
  askedFields.forEach(field => {
    if (!emitted.has(field.key)) emit(field, fields);
  });

  return { fields, conditionGroups, deciders: new Set(decider.values()) };
};
